import React, { ReactNode, useContext, useMemo, useState } from 'react';

import i18n from '@nextroll/ar-i18n';
import { useContainerContext } from '@nextroll/ar-container-context';
import { ScrollingTableHeader, ScrollingTable } from '@nextroll/ar-scrolling-table';

import { Alerts, Placements, Select, Option } from '@nextroll/ar-style-base';

import {
    WORKFLOW_DEFAULT_MAX_LIMIT, WORKFLOW_MAX_LIMIT_REACHED_ERROR, WORKFLOW_CLONE_TEMP_EID,
} from 'util/workflow.constants';
import { NotificationContext } from 'hooks/useNotification';

import { WorkflowStatusType, WORKFLOW_STATUS, IWorkflow, } from '../../../types/rofl';
import { DirType } from '../../../types/scrollingTable';
import { formatTimeShort } from '../../../util/util';
import { toggleWorkflowStatus, deleteWorkflow } from '../../../api/workflowsApi';

import { ActionType, IWorkflowTableRowColumn } from './WorkflowListTable.type';
import { ConfirmDialog } from '../ConfirmDialog/ConfirmDialog';

import { WorkflowTableNameCell } from './WorkflowListTableCells/WorkflowTableNameCell';
import { WorkflowTableTotalEnrolledCell } from './WorkflowListTableCells/WorkflowTableTotalEnrolledCell';
import { WorkflowTableEnrollmentStatsCell } from './WorkflowListTableCells/WorkflowTableEnrollmentStatsCell';
import {
    TotalEnrolledTooltip,
    MaxOnWorkflowsLimitReachedTooltip,
} from './WorkflowListTooltips/WorkflowListTooltips';


// constant values
const TIMEZONE = (formatTimeShort() || '').split(' ')[1] || '';
const DIALOG_CONTENT_CONTACT = {
    TURN_OFF: i18n.gettext('Once a workflow is turned off, contacts that meet the trigger criteria will no longer be enrolled in the workflow. Any actions that may be in process will finish executing.'),
    TURN_ON: i18n.gettext('When a workflow is turned on, contacts that meet the trigger criteria will be enrolled in the workflow from that point forward. Contacts that met the trigger criteria while the workflow was off will not be enrolled.'),
};
const DIALOG_CONTENT_ACCOUNT = {
    TURN_OFF: i18n.gettext('Once a workflow is turned off, accounts that meet the trigger criteria will no longer be enrolled in the workflow. Any actions that may be in process will finish executing.'),
    TURN_ON: i18n.gettext('When a workflow is turned on, accounts that meet the trigger criteria will be enrolled in the workflow from that point forward. Accounts that met the trigger criteria while the workflow was off will not be enrolled.'),
};
const STATUS_OPTIONS = [
    { display: i18n.gettext('All'), value: 'all' },
    { display: i18n.gettext('On'), value: 'on' },
    { display: i18n.gettext('Off'), value: 'off' },
];

/**
 * Element for displaying formatted workflow status, including proper colors and the dot in the front.
 */
const WorkflowStatus = ({ status }: { status?: string }) => (
    <span className={`workflows-workflow-list-status ${status === 'on' ? 'ar-label-success' : 'ar-label'}`}>
        <span>{'\u25CF'}</span>
        <span>{status}</span>
    </span>
);

/**
 * Element for displaying formatted timestamps, e.g. Sep 28, 2022 - 13:20 (timezone).
 */
const WorkflowTime = ({ time, subtext }: { time?: string; subtext?: string }) => {
    if (!time) {
        return <>{i18n.gettext('N/A')}</>;
    }
    return (
        <>
            <span>{i18n.moment(time).format('MMM D, YYYY - H:mm')}</span>
            <br />
            {subtext && <small>{subtext}</small>}
        </>
    );
};

/**
 * Element for the placholder when the worklist table is empty.
 */
const WorkflowBlankState = () => (
    <div className='ar-scrolling-table-blank-state'>
        <i className='fas fa-sitemap fa-3x center-block' />
        <br />
        {i18n.gettext('There aren\'t any workflows here at the moment.')}
    </div>
);

/**
 * Element for the tooltip when the user tries to turn on a workflow but the limit has been reached.
 * @param maxOnWorkflowsLimit The maximum number of workflows that can be in the 'on' state for the advertisable.
 * @param canTurnOnWorkflow Whether the user can turn on a workflow. This is true when the number of workflows in the
 * 'on' state is less than the limit.
 * @param placement The placement of the tooltip.
 * @constructor
 */
type StatusOptionType = 'all' | 'on' | 'off';
const WorkflowTableStatusSelect = ({ status, onSelect }: {
    status: StatusOptionType;
    onSelect: (value: StatusOptionType) => void;
}) => (
    <Select i18n={i18n} label={i18n.gettext('Status')} onOptionSelect={onSelect}>
        {STATUS_OPTIONS.map(option => (
            <Option key={option.value} value={option.value} selected={status === option.value}>
                {option.display}
            </Option>
        ))}
    </Select>
);

/**
 * List of possible actions in the action dropdown.
 */
const ACTIONS = (canTurnOnWorkflow?: boolean, maxOnWorkflowsLimit?: number) => ({ status }: Partial<IWorkflow>) => [
    {
        type: 'edit',
        label: i18n.gettext('Edit'),
    },
    {
        type: 'on-off',
        label: status === 'on' ? i18n.gettext('Turn Off') : (
            <MaxOnWorkflowsLimitReachedTooltip
                canTurnOnWorkflow={canTurnOnWorkflow}
                maxOnWorkflowsLimit={maxOnWorkflowsLimit}
                placement={Placements.LEFT}
            >
                <div style={{ height: '100%', width: '100%' }}>{i18n.gettext('Turn On')}</div>
            </MaxOnWorkflowsLimitReachedTooltip>
        ),
        disabled: status === 'off' && !canTurnOnWorkflow,
    },
    {
        type: 'clone',
        label: i18n.gettext('Clone'),
    },
    {
        type: 'delete',
        label: <span className='text-danger'>{i18n.gettext('Delete')}</span>,
    },
] as { type: ActionType; label: ReactNode; disabled?: boolean }[];

/**
 * Determine how the table rows are sorted.
 */
const sortHandler = (key: keyof IWorkflow) => (a: IWorkflow, b: IWorkflow) => {
    switch (key) {
        case 'total_enrolled_entities': return (a.total_enrolled_entities || 0) - (b.total_enrolled_entities || 0);
        case 'sum_of_enrolled_entities': return (a.sum_of_enrolled_entities || 0) - (b.sum_of_enrolled_entities || 0);
        default: return (a[key] as string || '').localeCompare(b[key] as string || '');
    }
};

/**
 * Determine the updated status when user toggles a workflow.
 */
const getUpdatedStatus = (status: WorkflowStatusType = WORKFLOW_STATUS.WORKFLOW_OFF) => {
    switch (status) {
        case WORKFLOW_STATUS.WORKFLOW_ON: return WORKFLOW_STATUS.WORKFLOW_OFF;
        case WORKFLOW_STATUS.WORKFLOW_OFF: return WORKFLOW_STATUS.WORKFLOW_ON;
        default: return status;
    }
};

interface IProps {
    loading?: boolean;
    data?: IWorkflow[];
    onUpdate?: (data: IWorkflow[]) => void;
    onSelect?: (row: IWorkflow) => void;
    canTurnOnWorkflow?: boolean;
    maxOnWorkflowsLimit?: number;
}

export const WorkflowListTable = (props: IProps) => {
    const { loading, data = [], canTurnOnWorkflow, maxOnWorkflowsLimit } = props;

    const { advertisable: { eid: advertisableEid } = {}, actualUser } = useContainerContext();

    const [openConfirmUpdateDialog, setOpenConfirmUpdateDialog] = useState(undefined as IWorkflow | undefined);
    const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(undefined as IWorkflow | undefined);

    const [sortBy, setSortBy] = useState({
        key: 'updated_at' as keyof IWorkflow,
        direction: ScrollingTable.SORT_DIRECTION_DESC as DirType,
    });
    const [searchValue, setSearchValue] = useState('');
    const [statusFilter, setStatusFilter] = useState('all' as StatusOptionType);

    const { addAlert } = useContext(NotificationContext);

    const COLUMNS: IWorkflowTableRowColumn[] = [
        {
            key: 'name',
            label: i18n.gettext('Name'),
            render: (workflow: IWorkflow) => (
                <WorkflowTableNameCell
                    name={workflow.name}
                    triggerErrors={workflow.errors?.schema_errors.trigger_errors || []}
                    actionErrors={workflow.errors?.schema_errors.action_errors || []}
                    triggerWarnings={workflow.warnings?.schema_warnings.trigger_warnings || []}
                    actionWarnings={workflow.warnings?.schema_warnings.action_warnings || []}
                    onSelect={() => props.onSelect?.(workflow)}
                />
            ),
            textAlignment: ScrollingTable.TEXT_ALIGN_LEFT,
            basis: 200,
            grow: 2,
        },
        {
            key: 'status',
            label: i18n.gettext('Status'),
            getCacheKey: (workflow: IWorkflow) => workflow.status,
            render: (workflow: IWorkflow) => <WorkflowStatus status={workflow.status} />,
            textAlignment: ScrollingTable.TEXT_ALIGN_LEFT,
        },
        {
            key: 'total_enrolled_entities',
            label: <TotalEnrolledTooltip><span>{i18n.gettext('Total Enrolled')}</span></TotalEnrolledTooltip>,
            render: (workflow: IWorkflow) => (
                <WorkflowTableTotalEnrolledCell workflow={workflow} actualUserRole={actualUser?.role} />
            ),
            textAlignment: ScrollingTable.TEXT_ALIGN_LEFT,
        },
        {
            key: 'sum_of_enrolled_entities',
            label: i18n.gettext('Enrolled Last 7 Days'),
            render: (workflow: IWorkflow) => <WorkflowTableEnrollmentStatsCell workflow={workflow} />,
            textAlignment: ScrollingTable.TEXT_ALIGN_LEFT,
        },
        {
            key: 'updated_at_name',
            label: `${i18n.gettext('Last Updated')} (${TIMEZONE})`,
            render: (workflow: IWorkflow) => <WorkflowTime time={workflow.updated_at} subtext={workflow.updated_by_name ? `by ${workflow.updated_by_name}` : ''} />,
            textAlignment: ScrollingTable.TEXT_ALIGN_LEFT,
        },
    ];

    // sorted data based on the value of 'sortBy'
    const sortedData = useMemo(() => {
        const sorted = [...data]
            .filter((row: IWorkflow) => statusFilter === 'all' || row.status === statusFilter) // filter by status dropdown select
            .filter((row: IWorkflow) => !searchValue || row.name.toLowerCase().includes(searchValue.toLowerCase())) // filter by search
            .sort(sortHandler(sortBy.key));
        return sortBy.direction === ScrollingTable.SORT_DIRECTION_ASC ? sorted : sorted.reverse();
    }, [data, statusFilter, searchValue, sortBy]);

    /**
     * Cache the content of the toggle on-off dialog.
     */
    const updateDialogContent = useMemo(() => {
        const status = openConfirmUpdateDialog?.status || 'off';
        const DIALOG_CONTENT = openConfirmUpdateDialog?.entity_type === 'account'
            ? DIALOG_CONTENT_ACCOUNT
            : DIALOG_CONTENT_CONTACT;
        return {
            title: i18n.gettext('Turn Workflow {{status}}', { status: getUpdatedStatus(status) }),
            description: status === 'on' ? DIALOG_CONTENT.TURN_OFF : DIALOG_CONTENT.TURN_ON,
            btnConfirm: status === 'on' ? i18n.gettext('Turn Workflow Off') : i18n.gettext('Turn Workflow On'),
            btnCancel: status === 'on' ? i18n.gettext('Keep On') : i18n.gettext('Cancel'),
        };
    }, [openConfirmUpdateDialog?.status, openConfirmUpdateDialog?.entity_type]);

    /**
     * Update table content with a callback that tells how to modify rows.
     */
    const updateDataWithOperation = (operation: (row: IWorkflow) => IWorkflow | undefined | false) => {
        const updatedData = [...data]
            .map(operation) // iterate through rows and apply the operation in the given callback
            .filter(Boolean); // remove unnecessery entries
        props.onUpdate?.(updatedData as IWorkflow[]);
    };

    /**
     * event handlers
     */
    const handleColSort = (col: keyof IWorkflow, direction: DirType) => setSortBy({ key: col, direction });

    const handleWorkflowTableRowAction = (row: IWorkflow, type: ActionType) => {
        switch (type) {
            case 'on-off': setOpenConfirmUpdateDialog(row); break;
            case 'edit': props.onSelect?.(row); break;
            case 'clone': props.onSelect?.({ ...row, eid: WORKFLOW_CLONE_TEMP_EID, name: `Clone of ${row.name}` }); break;
            case 'delete': setOpenConfirmDeleteDialog(row); break;
            default:
        }
    };

    /**
     * PUT workflow status
     */
    const handleOnOff = async () => {
        const { eid, status } = openConfirmUpdateDialog || {};
        if (!advertisableEid || !eid || !status) {
            return; // nothing to update; something is wrong
        }
        try {
            const updatedStatus = getUpdatedStatus(status);
            await toggleWorkflowStatus(eid, advertisableEid, updatedStatus);
            updateDataWithOperation(row => (row.eid === eid ? ({ ...row, status: updatedStatus }) : row));
        } catch (error: any) {
            const errorMsg = error?.message || JSON.stringify(error);

            if (error.parsedBody?.code === WORKFLOW_MAX_LIMIT_REACHED_ERROR) {
                addAlert?.({
                    type: Alerts.ALERT_TYPE_ERROR,
                    title: i18n.gettext('This workflow can\'t be turned on due to plan limits.'),
                    message: i18n.gettext(
                        'You reached your {{limit}} running workflows limit. Turn off one of your running workflows or Contact your Account Manager to increase your limit.',
                        { limit: WORKFLOW_DEFAULT_MAX_LIMIT },
                    ),
                });
            }
            console.error(errorMsg);
        }
    };

    /**
     * DELETE workflow
     */
    const handleDelete = async () => {
        if (!advertisableEid || !openConfirmDeleteDialog?.eid) {
            return; // nothing to delete; something is wrong
        }
        try {
            await deleteWorkflow(openConfirmDeleteDialog.eid, advertisableEid);
            updateDataWithOperation(row => row.eid !== openConfirmDeleteDialog.eid && row);
        } catch (error: any) {
            const errorMsg = error?.message || JSON.stringify(error);
            console.error(errorMsg);
        }
    };

    return (
        <div className='workflows-workflow-list-table ar-card'>
            <ScrollingTableHeader
                title={i18n.gettext('{{ count }} Workflows', { count: data.length })}
                searchValue={searchValue}
                onSearch={setSearchValue}
            >
                <WorkflowTableStatusSelect status={statusFilter} onSelect={setStatusFilter} />
            </ScrollingTableHeader>
            <ScrollingTable
                i18n={i18n}
                columns={COLUMNS}
                rowKey='eid'
                isLoading={loading}
                data={sortedData}
                stickyHeader
                totalCount={data.length}
                sort={sortBy}
                onSort={handleColSort}
                rowActions={ACTIONS(canTurnOnWorkflow, maxOnWorkflowsLimit)}
                onRowActionSelect={handleWorkflowTableRowAction}
                noResultsContent={<WorkflowBlankState />}
            />

            <ConfirmDialog
                show={!!openConfirmUpdateDialog}
                onClose={() => setOpenConfirmUpdateDialog(undefined)}
                title={updateDialogContent.title}
                description={updateDialogContent.description}
                btnConfirmLabel={updateDialogContent.btnConfirm}
                btnCancelLabel={updateDialogContent.btnCancel}
                isReverseActions={openConfirmUpdateDialog?.status === 'on'}
                request={handleOnOff}
            />

            <ConfirmDialog
                show={!!openConfirmDeleteDialog}
                onClose={() => setOpenConfirmDeleteDialog(undefined)}
                title={i18n.gettext('Delete Workflow')}
                description={i18n.gettext('Are you sure you want to delete "{{name}}"?', { name: openConfirmDeleteDialog?.name })}
                btnConfirmLabel={i18n.gettext('Delete')}
                isDangerColor
                confirmationText='DELETE'
                request={handleDelete}
            />
        </div>
    );
};
